import type { InlineItem } from 'components/form/types';
import type { Position } from 'modules/campaign/row';
import {
  addDays,
  differenceInDays,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  formatISO,
  parseISO,
  subDays,
} from 'date-fns';
import type { FieldArrayRenderProps } from 'react-final-form-arrays';
import type { Interval } from '../types';
import invariant from 'tiny-invariant';

export function splitGap(
  interval: Interval,
  split: 'full' | 'month' | 'week',
): { date_from: string; date_to: string }[] {
  if (split === 'full') {
    return [
      {
        date_from: formatISO(interval.start, { representation: 'date' }),
        date_to: formatISO(interval.end, { representation: 'date' }),
      },
    ];
  }
  if (split === 'month') {
    return eachMonthOfInterval(interval).map((monthStart, i, arr) => ({
      date_from: formatISO(i === 0 ? interval.start : monthStart, { representation: 'date' }),
      date_to: formatISO(i === arr.length - 1 ? interval.end : endOfMonth(monthStart), { representation: 'date' }),
    }));
  }
  return eachWeekOfInterval(interval, { weekStartsOn: 1 }).map((weekStart, i, arr) => ({
    date_from: formatISO(i === 0 ? interval.start : weekStart, { representation: 'date' }),
    date_to: formatISO(i === arr.length - 1 ? interval.end : endOfWeek(weekStart, { weekStartsOn: 1 }), {
      representation: 'date',
    }),
  }));
}

export const positionkey = (position: InlineItem<Position>): string => {
  return position.id ? `position-${position.id}` : `position-new-${position.__NEW__}`;
};

export type PositionItem = {
  type: 'position';
  key_: string;
  name: string;
  // form index
  index: number;
  interval: Interval;
  position: InlineItem<Position>;
  minDate: Date;
  maxDate: Date;
};

export type GapItem = {
  type: 'gap';
  key_: string;
  interval: Interval;
  // form index where a new position should be inserted
  formIndex: number;
};

export type DisplayItem = PositionItem | GapItem;

export function insertPosition(
  { date_from, date_to, quantity }: { date_from: string; date_to: string; quantity: number | null },
  positions: InlineItem<Position>[],
): [positions: InlineItem<Position>[], key_: string] {
  const __NEW__ = Date.now();

  const newPosition = {
    date_from,
    date_to,
    quantity,
    __NEW__,
  };
  const index = positions.findIndex((p) => p.date_from > date_from);
  const result = [...positions] as any[];
  if (index === -1) {
    result.push(newPosition);
  } else {
    result.splice(index, 0, newPosition);
  }
  return [result, positionkey(newPosition as any)];
}

export const collectItems = (
  fields: FieldArrayRenderProps<InlineItem<Position>, any>['fields'],
  totalInterval: Interval,
): DisplayItem[] => {
  const items: DisplayItem[] = [];

  let nextStart = totalInterval.start;

  fields.forEach((name, index) => {
    const position = fields.value[index];
    const maxDate =
      index < fields.value.length - 1 ? subDays(parseISO(fields.value[index + 1].date_from), 1) : totalInterval.end;

    const item: PositionItem = {
      type: 'position',
      key_: positionkey(position),
      name,
      index,
      interval: {
        start: parseISO(position.date_from),
        end: parseISO(position.date_to),
      },
      position,
      minDate: nextStart,
      maxDate,
    };
    const gapBefore = differenceInDays(item.interval.start, nextStart);
    invariant(gapBefore >= 0);
    if (gapBefore > 0) {
      items.push({
        type: 'gap',
        key_: `gap-${index}`,
        interval: {
          start: nextStart,
          end: subDays(item.interval.start, 1),
        },
        formIndex: index,
      });
    }
    items.push(item);

    nextStart = addDays(item.interval.end, 1);
  });

  const tailGap = differenceInDays(addDays(totalInterval.end, 1), nextStart);
  invariant(tailGap >= 0);

  if (tailGap > 0) {
    items.push({
      type: 'gap',
      key_: `gap-tail`,
      interval: {
        start: nextStart,
        end: totalInterval.end,
      },
      formIndex: fields.length ?? 0,
    });
  }

  return items;
};
